import React, { useCallback, useState } from 'react';
import { Button } from '@roger/ui-components';

import {
  acknowledgeAllPatientImportReviewItemsPatientsPatientIdChartImportsReviewItemsAcknowledgeAllPost as acknowledgeAllReviewItems,
  acknowledgePatientImportReviewItemPatientsPatientIdChartImportsReviewItemsItemIdAcknowledgePost as acknowledgeReviewItem,
  listPatientImportReviewItemsPatientsPatientIdChartImportsReviewItemsGet as listPatientImportReviewItems,
} from '../../services/api/sdk.gen';
import type { ImportReviewItemRead } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { formatDateInPT } from '../../utils/dateUtils';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import KinnserLink from './KinnserLink';
import {
  ACKNOWLEDGE_ALL_LABEL,
  ACKNOWLEDGE_ERROR_COPY,
  CHOICE_LABELS,
  EXTERNAL_VALUE_LABEL,
  REVIEW_ITEMS_ERROR_COPY,
  REVIEW_ITEMS_SETTLED_HEADING,
  ROGER_VALUE_LABEL,
  formatReviewValue,
  reviewItemsHeading,
  settledLine,
  showSettledLabel,
  type ReviewChoice,
} from './importReviewCopy';

interface ImportReviewItemsProps {
  patientId: string;
  items: ImportReviewItemRead[];
  /** Whether the reader holds the acknowledgement permission. */
  canAcknowledge: boolean;
  /** Hands the caller the list after one item, or all of them, settled. */
  onItemsChanged: (items: ImportReviewItemRead[]) => void;
}

/** The server refused because the item moved since it was shown. */
const CONFLICT = 409;

/** The External EMR list exactly as this row displays it, for the server to check. */
function externalValueSeen(item: ImportReviewItemRead): string[] {
  return Array.isArray(item.external_value)
    ? item.external_value.map(String)
    : [];
}

/**
 * One item: the sentence, the two values, and the answers it allows.
 *
 * The values are shown even when the reader cannot answer: the question is
 * worth reading on its own, and the buttons are the only thing the
 * permission withholds.
 */
const ReviewItemRow: React.FC<{
  item: ImportReviewItemRead;
  canAcknowledge: boolean;
  busy: boolean;
  onChoose: (item: ImportReviewItemRead, choice: ReviewChoice) => void;
}> = ({ item, canAcknowledge, busy, onChoose }) => {
  const settled =
    item.acknowledged_at !== null && item.acknowledged_at !== undefined;
  return (
    <li
      className='space-y-2 rounded-md border p-3'
      data-testid='import-review-item'
      data-settled={settled}
    >
      <p className='text-sm'>{item.message}</p>
      <dl className='grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2'>
        <div>
          <dt className='text-xs font-medium text-muted-foreground'>
            {ROGER_VALUE_LABEL}
          </dt>
          <dd data-testid='import-review-roger-value'>
            {formatReviewValue(item.roger_value)}
          </dd>
        </div>
        <div>
          <dt className='text-xs font-medium text-muted-foreground'>
            {EXTERNAL_VALUE_LABEL}
          </dt>
          <dd data-testid='import-review-external-value'>
            {formatReviewValue(item.external_value)}
          </dd>
        </div>
      </dl>
      <div className='flex flex-wrap items-center gap-2'>
        {item.url && <KinnserLink url={item.url} />}
        {settled ? (
          <p
            className='text-sm text-muted-foreground'
            data-testid='import-review-settled'
          >
            {settledLine(
              item.acknowledged_by_name,
              formatDateInPT(item.acknowledged_at as string),
              item.acknowledgement
            )}
          </p>
        ) : (
          canAcknowledge &&
          (item.choices ?? []).map(choice => (
            <Button
              key={choice}
              size='sm'
              variant={choice === 'take_external' ? 'default' : 'outline'}
              disabled={busy}
              onClick={() => onChoose(item, choice)}
            >
              {CHOICE_LABELS[choice]}
            </Button>
          ))
        )}
      </div>
    </li>
  );
};

/**
 * What a refresh left for a person to settle, above the run's report.
 *
 * Open items come first and carry the count in the heading; settled ones
 * fold under a toggle so the record of who chose what stays reachable. Every
 * answer is recorded by the server with the person and the time; the list
 * here is replaced with what the server sends back, never edited locally.
 * "Take External EMR's" sends the list this row showed, and the server
 * refuses the write when the item has moved since.
 */
const ImportReviewItems: React.FC<ImportReviewItemsProps> = ({
  patientId,
  items,
  canAcknowledge,
  onItemsChanged,
}) => {
  const showError = useStore(state => state.showError);
  const [busy, setBusy] = useState(false);
  const [showSettled, setShowSettled] = useState(false);

  const open = items.filter(item => !item.acknowledged_at);
  const settled = items.filter(item => Boolean(item.acknowledged_at));

  /**
   * The chart's items as the server has them now.
   *
   * Read after acknowledge-all (its response is only a count) and after a
   * conflict (the item moved under the reader), so what is on screen is
   * what the server recorded — the names and times it stamped included.
   */
  const reload = useCallback(async (): Promise<boolean> => {
    try {
      const { data } = await listPatientImportReviewItems({
        path: { patient_id: patientId },
        query: { include_acknowledged: true },
        throwOnError: true,
      });
      onItemsChanged(data?.items ?? []);
      return true;
    } catch (err) {
      console.error('Failed to reload the import review items', err);
      showError(REVIEW_ITEMS_ERROR_COPY);
      return false;
    }
  }, [onItemsChanged, patientId, showError]);

  const choose = useCallback(
    async (item: ImportReviewItemRead, choice: ReviewChoice) => {
      setBusy(true);
      try {
        const { data } = await acknowledgeReviewItem({
          path: { patient_id: patientId, item_id: item.id },
          body: {
            choice,
            external_value_seen:
              choice === 'take_external' ? externalValueSeen(item) : undefined,
          },
          throwOnError: true,
        });
        if (data) {
          onItemsChanged(items.map(row => (row.id === data.id ? data : row)));
        }
      } catch (err) {
        console.error('Failed to acknowledge the import review item', err);
        showError(getBusinessErrorMessage(err) ?? ACKNOWLEDGE_ERROR_COPY);
        if (getHttpStatus(err) === CONFLICT) await reload();
      } finally {
        setBusy(false);
      }
    },
    [items, onItemsChanged, patientId, reload, showError]
  );

  const acknowledgeAll = useCallback(async () => {
    setBusy(true);
    try {
      await acknowledgeAllReviewItems({
        path: { patient_id: patientId },
        throwOnError: true,
      });
      // The server settled every open item; its response is only a count.
      // Read the rows back so each settled line names the person and the
      // time the server recorded, not a guess made here.
      await reload();
    } catch (err) {
      console.error('Failed to acknowledge the import review items', err);
      showError(getBusinessErrorMessage(err) ?? ACKNOWLEDGE_ERROR_COPY);
    } finally {
      setBusy(false);
    }
  }, [patientId, reload, showError]);

  if (items.length === 0) return null;

  return (
    <section className='space-y-2' data-testid='import-review-items'>
      {open.length > 0 ? (
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <h3
            className='text-sm font-semibold'
            data-testid='import-review-heading'
          >
            {reviewItemsHeading(open.length)}
          </h3>
          {canAcknowledge && open.length > 1 && (
            <Button
              size='sm'
              variant='outline'
              disabled={busy}
              onClick={() => void acknowledgeAll()}
            >
              {ACKNOWLEDGE_ALL_LABEL}
            </Button>
          )}
        </div>
      ) : (
        <h3
          className='text-sm font-semibold'
          data-testid='import-review-heading'
        >
          {REVIEW_ITEMS_SETTLED_HEADING}
        </h3>
      )}
      {open.length > 0 && (
        <ul className='space-y-2'>
          {open.map(item => (
            <ReviewItemRow
              key={item.id}
              item={item}
              canAcknowledge={canAcknowledge}
              busy={busy}
              onChoose={(row, choice) => void choose(row, choice)}
            />
          ))}
        </ul>
      )}
      {settled.length > 0 && (
        <>
          {open.length > 0 && (
            <Button
              variant='ghost'
              size='sm'
              aria-expanded={showSettled}
              onClick={() => setShowSettled(value => !value)}
            >
              {showSettledLabel(settled.length, showSettled)}
            </Button>
          )}
          {(showSettled || open.length === 0) && (
            <ul className='space-y-2' data-testid='import-review-settled-list'>
              {settled.map(item => (
                <ReviewItemRow
                  key={item.id}
                  item={item}
                  canAcknowledge={false}
                  busy={busy}
                  onChoose={() => undefined}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

export default ImportReviewItems;
